import { Link, Typography } from '@mui/material';
import { t } from 'i18next';
import { TraceType, getTraceTypeTracer } from '../../../../common/enums/TraceType';
import { DateUtils } from '../../../../common/utils/DateUtils';
import { UrlHelper } from '../../../../common/utils/UrlHelper';
import { PATHS } from '../../../../routing/routes';
import { ReportPrintData } from '../../report/models/ReportPrintData';
import { Header, HeaderItem } from './Header';

type Props = {
  report: ReportPrintData;
};

export const ReportSummaryDetails = ({ report }: Props) => {
  const trace = report.getTrace();
  const profile = report.getProfile();
  const project = report.getProject();
  const nonsensitiveSalt = report.getNonsensitiveSalt();
  const coverage = report.getCoverage();

  const items: HeaderItem[] = [
    {
      label: t('generatedAt'),
      value: DateUtils.toDateTimeString(report.getGeneratedAt()),
    },
    {
      label: t('completedIn'),
      value: DateUtils.toDurationString(report.getGeneratedAt(), report.getCompletedAt()),
    },
  ];
  if (trace) {
    const traceToolName = getTraceTypeTracer(trace.getCategory() as TraceType);
    const traceToolLabel = nonsensitiveSalt
      ? t('reportGeneratedWithProductionSafeTool', { tool: traceToolName })
      : t('reportGeneratedWithStandardTool', { tool: traceToolName });

    items.push({
      label: t('trace'),
      value: (
        <Typography>
          {trace.getLabel()}
          <Typography component="span" color="text.secondary" sx={{ pl: 0.5 }}>
            ({traceToolLabel})
          </Typography>
        </Typography>
      ),
    });
  }
  if (project) {
    items.push({
      label: t('project'),
      value: <Link href={UrlHelper.generatePath(PATHS.PROJECT_DASHBOARD, { projectId: project.getId() })}>{project.getLabel()}</Link>,
    });
  }
  if (profile) {
    items.push({
      label: t('profile'),
      value: <Link href={UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: profile.getId().toString() })}>{profile.getLabel()}</Link>,
    });
  }
  if (coverage) {
    const coverageLabel =
      coverage.getDepth() !== null
        ? t('reportCoverageWithDepth', { covered: coverage.getCovered(), total: coverage.getTotal(), depth: coverage.getDepth() })
        : t('reportCoverage', { covered: coverage.getCovered(), total: coverage.getTotal() });

    items.push({
      label: t('coverage'),
      value: (
        <Typography component="span">
          {coverage.getPercent()}%
          <Typography component="span" color="text.secondary" sx={{ pl: 0.5 }}>
            ({coverageLabel})
          </Typography>
        </Typography>
      ),
    });
  }
  if (nonsensitiveSalt) {
    items.push({
      label: t('saltForKeyHash'),
      value: nonsensitiveSalt,
    });
  }

  return <Header title={report.getName()} items={items} />;
};
